import { ImageName } from "@mcleod/images";
import { Label, Panel, TableRow, TableRowDisplayEvent, TableRowExpansionEvent } from "@mcleod/components";
import { AutogenLayoutIntercompanySlideout } from "./autogen/AutogenLayoutIntercompanySlideout";
import { IntercompanyDetail } from "./IntercompanyDetail";
import { Alignment, ModelRow, StringUtil, UserSettings } from "@mcleod/core";
import { DataFusionErrorPanel } from "./DataFusionErrorPanel";
import { CommonTooltips} from "@mcleod/common";

export class IntercompanySlideout extends AutogenLayoutIntercompanySlideout {
    private _movementId: string;
    private _ediOrderId: string;

    tableIntercompanyOnRowDisplay(event: TableRowDisplayEvent) {
        const currRow = event.target as TableRow;
        const currModelRow = currRow.data as ModelRow;
        const rowCount = event.getTable().rowCount;
        this.setErrorImages(currModelRow, currRow);
        const sharedOrder = currRow.findComponentById("labelSharedOrder") as Label;
        sharedOrder.addUnderlineWhenMouseOver();
        sharedOrder.tooltip = "Click to open the shared order in a new tab.\n(If using 'Keep me signed in' login option)";

        const moveStatus = currModelRow.get("move_status");
        if(StringUtil.isEmptyString(moveStatus)) {
            const moveStatusComp = currRow.findComponentById("labelMoveStatus") as Label;
            moveStatusComp.visible = false;
        }

        const role = currModelRow.get("role");
        const roleComp = currRow.findComponentById("labelRole") as Label;
        roleComp.text = StringUtil.equalsIgnoreCase("S", role) ? "Sender" : "Receiver";

        const replyComp = currRow.findComponentById("labelICReply") as Label;
        const replyStatus = currModelRow.get("reply_status");

        switch (replyStatus) {
            case "Accepted": {
                replyComp.color = "success";
                replyComp.imageName = ImageName.circleCheckEmpty;
                replyComp.text = "Accepted";
                break;
            }
            case "Declined": {
                replyComp.color = "error";
                replyComp.imageName = ImageName.circleX;
                replyComp.text = "Declined";
                break;
            }
            case "Not Received": {
                replyComp.color = "warning";
                replyComp.imageName = ImageName.circleX;
                replyComp.text = "Not Received";
                break;
            }
            case "Timed Out": {
                replyComp.color = "default";
                replyComp.imageName = ImageName.timeExpired;
                replyComp.text = "Timed Out";
                break;
            }
            case "Received": {
                replyComp.color = "success";
                replyComp.imageName = ImageName.circleCheckEmpty;
                replyComp.text = "Received";
                break;
            }
            case "Not Received": {
                replyComp.color = "warning";
                replyComp.imageName = ImageName.circleX;
                replyComp.text = "Not Received";
                break;
            }
            case undefined:
            case "": {
                replyComp.imageName = null;
                replyComp.text = null;
            }
        }

        if(currRow.index === rowCount-1) {
            currRow.expanderButton.clicked();
            currRow.expanded = true;
        }
    }

    tableIntercompanyOnRowExpand(event: TableRowExpansionEvent) {
        const icData = event.target.data as ModelRow;
        const expandPanel = event.expandComponentParent as Panel;
        const layoutICDetail = expandPanel.findComponentById("layoutIntercompanyDetail") as IntercompanyDetail;
        layoutICDetail.sharedStops = icData.get("shared_co_stops");
        layoutICDetail.shipperStopId = icData.get("origin_stop_id");
        layoutICDetail.consigneeStopId = icData.get("dest_stop_id");
        layoutICDetail.movementId = this.movementId;
        layoutICDetail.ediOrderId = this.ediOrderId;
        layoutICDetail.isBrokerage = StringUtil.equalsIgnoreCase("Y", icData.get("brokerage"));
        layoutICDetail.ops_name = icData.get("ops_user_name");
        layoutICDetail.ops_phone = icData.get("ops_user_phone");
        layoutICDetail.ops_ext = icData.get("ops_user_ext");
        layoutICDetail.ops_email = icData.get("ops_user_email");
        layoutICDetail.dis_name = icData.get("dis_user_name");
        layoutICDetail.dis_phone = icData.get("dis_user_phone");
        layoutICDetail.dis_ext = icData.get("dis_user_ext");
        layoutICDetail.dis_email = icData.get("dis_user_email");
        layoutICDetail.driver = icData.get("shared_co_driver");
        layoutICDetail.driverPhone = icData.get("shared_co_driver_phone");
        layoutICDetail.driverEmail = icData.get("shared_co_driver_email");
        layoutICDetail.driver2 = icData.get("shared_co_driver2");
        layoutICDetail.driver2Phone = icData.get("shared_co_driver2_phone");
        layoutICDetail.driver2Email = icData.get("shared_co_driver2_email");
        layoutICDetail.tractor = icData.get("shared_co_tractor");
        layoutICDetail.trailer = icData.get("shared_co_trailer");
        layoutICDetail.carrierName = icData.get("shared_co_carrier_name");
        layoutICDetail.setIntercompanyData();
    }

    setErrorImages(currRow: ModelRow, currTableRow: TableRow) {
        const errorLayout = currTableRow.findComponentById("layoutDataFusionErrorPanel") as DataFusionErrorPanel;
        const inTenderErrors = currRow.get("in_tender_errors");
        const outTenderErrors = currRow.get("out_tender_errors");
        const statusDesc = currRow.get("status_desc");
        const inStatusErrors = currRow.get("in_status_errors");
        const outStatusErrors = currRow.get("out_status_errors");
        const inInvoiceErrors = currRow.get("in_invoice_errors");
        const outInvoiceErrors = currRow.get("out_invoice_errors");

        if (inTenderErrors && inTenderErrors.length > 0) {
            errorLayout.imageInTenderErr.visible = true;
            errorLayout.horizontalspacer1.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageInTenderErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "in_tender_errors": inTenderErrors
                }
            );
            errorLayout.imageInTenderErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageInTenderErr.visible = false;
        }

        if(outTenderErrors && outTenderErrors.length > 0) {
            errorLayout.imageOutTenderErr.visible = true;
            errorLayout.horizontalspacer2.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageOutTenderErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "out_tender_errors": outTenderErrors
                }
            );
            errorLayout.imageOutTenderErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageOutTenderErr.visible = false;

        }

        if(inStatusErrors && inStatusErrors.length > 0) {
            errorLayout.imageInStatusErr.visible = true;
            errorLayout.horizontalspacer3.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageInStatusErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "in_status_errors": inStatusErrors, "status_desc": statusDesc
                }
            );
            errorLayout.imageInStatusErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageInStatusErr.visible = false;
        }

        if(outStatusErrors && outStatusErrors.length > 0) {
            errorLayout.imageOutStatusErr.visible = true;
            errorLayout.horizontalspacer4.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageOutStatusErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "out_status_errors": outStatusErrors, "status_desc": statusDesc
                }
            );
            errorLayout.imageOutStatusErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageOutStatusErr.visible = false;
        }

        if(inInvoiceErrors && inInvoiceErrors.length > 0) {
            errorLayout.imageInInvoiceErr.visible = true;
            errorLayout.horizontalspacer5.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageInInvoiceErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "in_invoice_errors": inInvoiceErrors
                }
            );
            errorLayout.imageInInvoiceErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageInInvoiceErr.visible = false;
        }

        if(outInvoiceErrors && outInvoiceErrors.length > 0) {
            errorLayout.imageOutInvoiceErr.visible = true;

            CommonTooltips.setTooltipContentLayout(errorLayout.imageOutInvoiceErr, null, "lme/datafusion/DataFusionErrorQuickInfo", null,
                {
                    "out_invoice_errors": outInvoiceErrors
                }
            );
            errorLayout.imageOutInvoiceErr.tooltipDynamicOptions = { position: Alignment.LEFT };
        } else {
            errorLayout.imageOutInvoiceErr.visible = false;
        }
    }

    sharedOrderOnClick(event: MouseEvent) {
        const orderLabel = event.target as unknown as Label;
        const data = orderLabel.boundRow.data as ModelRow;
        const mode = "?mode=update&key=";
        const sharedCompany: string = StringUtil.stringBefore(data["shared_company"], " ")?.toLowerCase();
        const sharedOrder = data["shared_order_id"];
        const currentCompany: string = UserSettings.get()?.company_id?.toLowerCase();
        const pathname = "/lme/dispatch/OrderTabView";

        const myLink = this.buildSharedCompanyUrl(sharedCompany, currentCompany, pathname, mode, sharedOrder);
        window.open(myLink, '_blank');
    }

    private buildSharedCompanyUrl(sharedCompany: string, currentCompany: string, pathname: string, mode: string, sharedOrder: string): string {
        const currentUrl = new URL(window.location.href);
        const protocol = currentUrl.protocol;
        const hostname = currentUrl.hostname;
        const port = currentUrl.port;

        // Build port string (include colon if port exists)
        const portString = port ? `:${port}` : '';

        // Check if current URL uses subdomain format (company-domain.com)
        const dashIndex = hostname.indexOf('-');
        if (dashIndex > 0) {
            // Subdomain format: replace company prefix before dash
            const domainPart = hostname.substring(dashIndex); // includes the dash
            return `${protocol}//${sharedCompany}${domainPart}${portString}${pathname}${mode}${sharedOrder}`;
        }

        // Check if current URL uses domain format (tms/tms2/tms3.company.com)
        const dotIndex = hostname.indexOf('.');
        if (dotIndex > 0) {
            // Domain format: replace the subdomain (tms/tms2/tms3) with tms + company
            const currentSubdomain = hostname.substring(0, dotIndex);
            const domainPart = hostname.substring(dotIndex); // includes the dot and everything after

            // Extract the tms prefix (tms, tms2, tms3, etc.) to maintain the same server instance
            const tmsPrefix = currentSubdomain.match(/^tms\d*/) ? currentSubdomain.match(/^tms\d*/)[0] : 'tms';

            return `${protocol}//${tmsPrefix}.${sharedCompany}${domainPart}${portString}${pathname}${mode}${sharedOrder}`;
        }

        // Fallback: if we can't determine the pattern, try to replace the current company in the hostname
        const modifiedHostname = hostname.replace(currentCompany, sharedCompany);
        return `${protocol}//${modifiedHostname}${portString}${pathname}${mode}${sharedOrder}`;
    }

    public get movementId(): string {
        return this._movementId;
    }

    public set movementId(value: string) {
        this._movementId = value;
    }

    public get ediOrderId(): string {
        return this._ediOrderId;
    }

    public set ediOrderId(value: string) {
        this._ediOrderId = value;
    }
}
